import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { format } from "date-fns";
import { useReactToPrint } from "react-to-print";
import {
  FaFilter,
  FaColumns,
  FaExpand,
  FaCompress,
  FaLayerGroup,
  FaChevronRight,
  FaChevronLeft,
} from "react-icons/fa";
import { IoIosArrowDown, IoMdClose } from "react-icons/io";
import { IoCodeSharp } from "react-icons/io5";
import { MdOutlineSegment, MdOutlineFileDownload } from "react-icons/md";
import {
  Checkbox,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  OutlinedInput,
  ListItemText,
  Button,
} from "@mui/material";

import ModifyColumns from "./Tools/ModifyColumns";
import { DateRangeSelector } from "./../styles/CustomDateRangePicker";

import {
  fetchData,
  fetchAdsCompareData,
  fetchSegmentDeviceData,
  fetchSegmentNetworkData,
  fetchSegmentClickTypeData,
  fetchSegmentTopVsOtherData,
  fetchSegmentDateData,
  fetchGroupData,
  fetchSegmentDateGroupData,
} from "./Components/Ads/AdsFetch";

import {
  apiRequestBodyBuilder,
  FilterComponent,
  getFilteredData,
  getPriorityColumnsFromStorage,
  getSortableValueGLOBAL,
  renderTableCell,
  renderTableTotal,
} from "./config/tableHelper";
import { RenderTable } from "./config/tableWrapper";
import { generatePDF, generateCSV } from "./Components/DownloadFuncs";

// Skeleton styled identically to PaidAdsPage TableSkeleton
function TableSkeleton() {
  return (
    <div className="w-full rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      {/* Header Bar */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-100 animate-pulse">
        <div className="h-4 w-32 bg-gray-200 rounded" />
        <div className="flex items-center gap-8 sm:gap-12">
          <div className="h-4 w-16 bg-gray-200 rounded hidden sm:block" />
          <div className="h-4 w-16 bg-gray-200 rounded" />
          <div className="h-4 w-20 bg-gray-200 rounded" />
          <div className="h-4 w-24 bg-gray-200 rounded hidden md:block" />
        </div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-gray-100 animate-pulse">
        {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
          <div key={item} className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <div
                className="h-4 bg-gray-200 rounded"
                style={{ width: `${140 + (item % 3) * 35}px` }}
              />
            </div>
            <div className="flex items-center gap-8 sm:gap-12">
              <div className="h-4 w-14 bg-gray-100 rounded hidden sm:block" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-20 bg-gray-100 rounded hidden md:block" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const Ads = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterArray, setFilterArray] = useState([]);
  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  const [columns, setColumns] = useState([]);
  const [tempColumns, setTempColumns] = useState(columns);
  const [tableVisible, setTableVisible] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showInitialData, setShowInitialData] = useState(true);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [random, setRandom] = useState(Math.random());
  const [isGroupDropdownVisible, setGroupDropdownVisible] = useState(false);
  const [savedGroups, setSavedGroups] = useState([]);
  const [groupNames, setGroupNames] = useState([]);
  const [totalShow, setTotalShow] = useState(true);
  const [showDatewiseType, setShowDatewiseType] = useState(false);
  const [selectedGroupIds, SetSelectedGroupIds] = useState([]);
  const [currentGroupType, setCurrentGroupType] = useState("");
  const [segmentType, setSegmentType] = useState("default");
  const [viewMode, setViewMode] = useState("ads");
  const [total, setTotal] = useState({});
  const [isGroupListVisible, setIsGroupListVisible] = useState(false);
  const [showViewBy, setShowViewBy] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [currentOrderType, setCurrentOrderType] = useState(true);
  const [currentOrderVariable, setCurrentOrderVariable] = useState(null);

  const [isDownloading, setIsDownloading] = useState(false);

  let d = new Date();
  let year = d.getFullYear();
  let month = d.getMonth();

  const [state, setState] = useState([
    {
      startDate: new Date(year, month, 1),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  let today = new Date();
  let priorDate = new Date(today);
  priorDate.setDate(today.getDate() - 30);
  today = today.toJSON().slice(0, 10);
  priorDate = priorDate.toJSON().slice(0, 10);

  const [viewByObject, setViewbyObject] = useState({
    groupBy: "date",
    startDate: priorDate,
    endDate: today,
  });

  const datePickerRef = useRef(null);
  const SegmentRef = useRef(null);
  const CategoryRef = useRef(null);
  const ColumnRef = useRef(null);
  const GroupsRef = useRef(null);
  const exportRef = useRef(null);
  const downloadRef = useRef(null);

  // FILE DOWNLOAD INTEGRATION
  const handleDownload = async (type) => {
    setShowDownloadMenu(false);
    setIsDownloading(true);
    try {
      const filename = `Ads_Report_${format(new Date(), "yyyy-MM-dd")}.${type}`;
      const dateRangeStr =
        state[0].startDate === state[0].endDate
          ? "All time"
          : `${format(state[0].startDate, "MMM dd, yyyy")} - ${format(state[0].endDate, "MMM dd, yyyy")}`;

      await new Promise((resolve) => setTimeout(resolve, 100));

      if (type === "pdf") {
        await generatePDF(
          filteredData,
          columns,
          dateRangeStr,
          filename,
          "Ads report",
        );
      } else if (type === "csv") {
        await generateCSV(
          filteredData,
          columns,
          dateRangeStr,
          filename,
          "Ads report",
        );
      }
    } catch (error) {
      console.error(`Failed to generate ${type.toUpperCase()}:`, error);
    } finally {
      setIsDownloading(false);
    }
  };

  const getSelectedGroupNames = (selectedIds) =>
    savedGroups
      .filter((g) => selectedIds.includes(g.group_id))
      .map((g) => g.group_name)
      .join(", ");

  const handleGroupChange = (event) => SetSelectedGroupIds(event.target.value);

  const toggleGroupSegment = () => {
    if (viewMode === "groups") {
      setViewMode("ads");
      setGroupDropdownVisible(false);
    } else {
      toggleDropDownTabs("Category");
    }
  };

  const fetchGroups = async () => {
    try {
      let requestBody = apiRequestBodyBuilder();
      const responseInfo = await axios.post(
        "https://api.confidanto.com/campaign-group2/group-info",
        requestBody,
      );

      let typeForQuery = "campaigns";
      if (responseInfo.data && responseInfo.data.type) {
        const TYPEQUERY =
          responseInfo.data.type?.charAt(0).toUpperCase() +
          responseInfo.data.type?.slice(1);
        setCurrentGroupType(TYPEQUERY);
        typeForQuery =
          responseInfo.data.type === "keyword"
            ? "keywords"
            : responseInfo.data?.type;
      }
      requestBody = apiRequestBodyBuilder({ type: typeForQuery });
      const response = await axios.post(
        "https://api.confidanto.com/campaign-group/fetch-groups",
        requestBody,
      );

      if (response.data.groups) {
        const groupsData = response.data.groups;
        setSavedGroups(groupsData);
        const names = groupsData.map((group) => group.group_name);
        setGroupNames(names);
      }
    } catch (error) {
      console.error("Error fetching campaign groups:", error);
    }
  };

  const changeGroupbyView = (segment, dateSegment = "none") => {
    let newObj = viewByObject;
    newObj.groupBy = dateSegment;
    setViewbyObject(newObj);
    setSegmentType(segment);
    setRandom(Math.random());
    toggleDropDownTabs("Hide all");
  };

  useEffect(() => {
    const loadDataAndColumns = async () => {
      setIsLoading(true);
      setData([]);
      setError(null);
      setCurrentPage(1);
      try {
        switch (segmentType) {
          case "default":
            if (viewMode === "groups") {
              await fetchGroupData(
                state,
                setColumns,
                setData,
                setTotal,
                setError,
                selectedGroupIds,
                currentGroupType,
              );
            } else {
              if (state.length > 1) {
                await fetchAdsCompareData(
                  state,
                  setData,
                  setShowDatePicker,
                  setTotal,
                  setColumns,
                );
              } else {
                await fetchData(state, setColumns, setData, setTotal, setError);
              }
            }
            break;
          case "segment_date":
            if (viewMode === "groups") {
              await fetchSegmentDateGroupData(
                state,
                setColumns,
                setData,
                setTotal,
                viewByObject,
                selectedGroupIds,
                currentGroupType,
              );
            } else {
              await fetchSegmentDateData(
                state,
                setColumns,
                setData,
                setTotal,
                viewByObject,
              );
            }
            break;
          case "segment_device":
            await fetchSegmentDeviceData(state, setColumns, setData, setTotal);
            break;
          case "segment_network":
            await fetchSegmentNetworkData(state, setColumns, setData, setTotal);
            break;
          case "segment_clicktype":
            await fetchSegmentClickTypeData(
              state,
              setColumns,
              setData,
              setTotal,
            );
            break;
          case "segment_topvsother":
            await fetchSegmentTopVsOtherData(
              state,
              setColumns,
              setData,
              setTotal,
            );
            break;
          default:
            break;
        }

        await getCustomColumns(setColumns, setTempColumns);
        await fetchGroups();
      } catch (error) {
        console.error("Error in useEffect data loading sequence:", error);
        setError(
          error.message || "An unexpected error occurred during data loading.",
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadDataAndColumns();
  }, [random, segmentType, viewMode]);

  const getCustomColumns = async (setColumns, setTempColumns) => {
    try {
      const requestBody = apiRequestBodyBuilder(null);

      const response = await axios.post(
        "https://api.confidanto.com/custom_columns/get-custom-columns",
        requestBody,
      );
      const resData = response.data.data;
      const temp = resData.map((i) => ({
        id: i.id,
        title: i.custom_column,
        key: i.custom_column,
        visible: false,
        category: "Custom Columns",
        type: "number",
      }));

      setColumns((prevColumns) => {
        const prevColsFiltered = prevColumns.filter(
          (item) => item.category !== "Custom Columns",
        );
        const allColumns = [...prevColsFiltered, ...temp];

        const sorted = getPriorityColumnsFromStorage(allColumns);

        return sorted;
      });
      if (setTempColumns) {
        setTempColumns((prevTempColumns) => [...prevTempColumns, ...temp]);
      }
    } catch (error) {
      setColumns((prevColumns) => {
        const sorted = getPriorityColumnsFromStorage(prevColumns);
        return sorted;
      });
      console.error("Error fetching custom columns:", error);
    }
  };

  const toggleFullScreen = () => setIsFullScreen(!isFullScreen);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInsideMuiMenu = event.target.closest(
        ".groupwise-select-menu",
      );
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      )
        setShowDatePicker(false);
      if (SegmentRef.current && !SegmentRef.current.contains(event.target))
        setIsGroupListVisible(false);
      if (CategoryRef.current && !CategoryRef.current.contains(event.target))
        setGroupDropdownVisible(false);
      if (ColumnRef.current && !ColumnRef.current.contains(event.target))
        setShowColumnsMenu(false);
      if (downloadRef.current && !downloadRef.current.contains(event.target)) {
        setShowDownloadMenu(false);
      }
      if (
        GroupsRef.current &&
        !GroupsRef.current.contains(event.target) &&
        !isClickInsideMuiMenu
      ) {
        setGroupDropdownVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleDropDownTabs = (type) => {
    setShowDatePicker(type === "Calendar" ? !showDatePicker : false);
    setIsGroupListVisible(type === "Segment" ? !isGroupListVisible : false);
    setGroupDropdownVisible(
      type === "Category" ? !isGroupDropdownVisible : false,
    );
    setShowColumnsMenu(type === "Columns" ? !showColumnsMenu : false);
    setShowDownloadMenu(type === "Download" ? !showDownloadMenu : false);
  };

  const changeOrderTypeCampaign = (type) => {
    const newOrderType =
      type !== currentOrderVariable ? true : !currentOrderType;
    setCurrentOrderType(newOrderType);
    setCurrentOrderVariable(type);

    const sortedData = [...data].sort((a, b) => {
      const valA = getSortableValueGLOBAL(a, type);
      const valB = getSortableValueGLOBAL(b, type);

      if (valA == null) return 1;
      if (valB == null) return -1;

      let comparison = 0;
      if (typeof valA === "number" && typeof valB === "number") {
        comparison = valA - valB;
      } else {
        comparison = String(valA).localeCompare(String(valB));
      }
      return newOrderType ? comparison : -comparison;
    });
    setData(sortedData);
  };

  const removeFilterFromArray = (id) => {
    setFilterArray((prev) => prev.filter((fil) => fil.id !== id));
  };

  const filteredData = useMemo(() => {
    return getFilteredData(data, filterArray);
  }, [data, filterArray]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  useEffect(() => {
    setColumns((prev) =>
      prev.map((col, index) => (index === 0 ? { ...col, visible: true } : col)),
    );
  }, []);
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage);

  const isFirstVisibleColumn = (columns, col) =>
    columns.filter((c) => c.visible)[0].key === col.key;

  return (
    <div ref={exportRef}>
      <div
        className={`flex h-screen bg-white ${
          isFullScreen ? "fixed top-0 left-0 w-full h-full z-50 " : ""
        }`}
      >
        <main className="flex flex-col flex-1 w-0 overflow-hidden p-6">
          <div
            className={`transition-opacity duration-300 ${isLoading ? "pointer-events-none opacity-50" : ""}`}
          >
            <DateRangeSelector
              state={state}
              setState={setState}
              setRandom={setRandom}
              showDatePicker={showDatePicker}
              toggleDropDownTabs={toggleDropDownTabs}
              datePickerRef={datePickerRef}
              Page="Ads"
            />
          </div>

          <div className="flex justify-between items-center mb-4">
            <div
              className={`transition-opacity duration-300 ${isLoading ? "pointer-events-none opacity-50" : ""}`}
            >
              <FilterComponent
                columns={columns}
                data={data}
                filterArray={filterArray}
                setFilterArray={setFilterArray}
                segmentType={viewByObject.groupBy}
              />
            </div>
            <div className="flex space-x-2">
              <div className="relative" ref={SegmentRef}>
                <button
                  disabled={isLoading}
                  className={`px-4 py-2 min-w-[80px] rounded flex flex-col justify-center items-center gap-1 transition-all ${
                    segmentType !== "default"
                      ? "bg-blue-100 text-blue-600"
                      : "bg-transparent text-gray-600"
                  } ${isLoading ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-100"}`}
                  onClick={() => toggleDropDownTabs("Segment")}
                >
                  <MdOutlineSegment className="text-md" />
                  <span className="text-md">Segment</span>
                </button>
                {isGroupListVisible && (
                  <div className="absolute z-40 w-56 bg-white shadow-lg rounded-lg mt-2 p-4 border border-gray-200">
                    <div
                      className="p-2 hover:bg-gray-100 capitalize cursor-pointer"
                      onClick={() => {
                        changeGroupbyView("default");
                        setViewMode("ads");
                      }}
                    >
                      Reset
                    </div>
                    <p className="p-2 text-sm text-gray-400">By</p>
                    <button
                      className="p-2 flex items-center hover:bg-gray-50 cursor-pointer w-full justify-between"
                      onClick={() => setShowViewBy(!showViewBy)}
                    >
                      View By{" "}
                      <IoIosArrowDown
                        className={`ml-2 transform transition-transform duration-300 ease-out text-xl ${
                          showViewBy ? "rotate-180" : "rotate-0"
                        }`}
                      />
                    </button>
                    {showViewBy && (
                      <ul className="mx-2">
                        <li>
                          <div className="relative w-full cursor-pointer">
                            <div
                              className="w-full text-left hover:bg-slate-50 h-10 p-2 rounded-sm flex justify-between items-center"
                              onClick={() =>
                                setShowDatewiseType(!showDatewiseType)
                              }
                            >
                              <h2>Date</h2>
                              <IoIosArrowDown
                                className={`transition-transform ${
                                  showDatewiseType ? "rotate-180" : ""
                                }`}
                              />
                            </div>
                            {showDatewiseType && (
                              <div className="pl-3 mt-0.5 flex flex-col space-y-1">
                                <li
                                  className={`p-2 capitalize cursor-pointer ${
                                    segmentType === "segment_date" &&
                                    viewByObject.groupBy === "date"
                                      ? "bg-blue-100 text-blue-600"
                                      : "hover:bg-gray-100"
                                  }`}
                                  onClick={() =>
                                    changeGroupbyView("segment_date", "date")
                                  }
                                >
                                  Day
                                </li>
                                <li
                                  className={`p-2 capitalize cursor-pointer ${
                                    segmentType === "segment_date" &&
                                    viewByObject.groupBy === "week"
                                      ? "bg-blue-100 text-blue-600"
                                      : "hover:bg-gray-100"
                                  }`}
                                  onClick={() =>
                                    changeGroupbyView("segment_date", "week")
                                  }
                                >
                                  Week
                                </li>
                                <li
                                  className={`p-2 capitalize cursor-pointer ${
                                    segmentType === "segment_date" &&
                                    viewByObject.groupBy === "month"
                                      ? "bg-blue-100 text-blue-600"
                                      : "hover:bg-gray-100"
                                  }`}
                                  onClick={() =>
                                    changeGroupbyView("segment_date", "month")
                                  }
                                >
                                  Month
                                </li>
                              </div>
                            )}
                          </div>
                        </li>
                        <li
                          className={`p-2 capitalize cursor-pointer ${
                            segmentType === "segment_device"
                              ? "bg-blue-100 text-blue-600"
                              : "hover:bg-gray-100"
                          }`}
                          onClick={() => changeGroupbyView("segment_device")}
                        >
                          Device
                        </li>
                        <li
                          className={`p-2 capitalize cursor-pointer ${
                            segmentType === "segment_network"
                              ? "bg-blue-100 text-blue-600"
                              : "hover:bg-gray-100"
                          }`}
                          onClick={() => changeGroupbyView("segment_network")}
                        >
                          Network
                        </li>
                        <li
                          className={`p-2 capitalize cursor-pointer ${
                            segmentType === "segment_clicktype"
                              ? "bg-blue-100 text-blue-600"
                              : "hover:bg-gray-100"
                          }`}
                          onClick={() => changeGroupbyView("segment_clicktype")}
                        >
                          Click Type
                        </li>
                        <li
                          className={`p-2 capitalize cursor-pointer ${
                            segmentType === "segment_topvsother"
                              ? "bg-blue-100 text-blue-600"
                              : "hover:bg-gray-100"
                          }`}
                          onClick={() =>
                            changeGroupbyView("segment_topvsother")
                          }
                        >
                          Top vs Other
                        </li>
                      </ul>
                    )}
                  </div>
                )}
              </div>
              <div className="relative" ref={GroupsRef}>
                <button
                  disabled={isLoading}
                  className={`px-4 py-2 min-w-[80px] rounded flex flex-col justify-center items-center gap-1 transition-all ${
                    isLoading
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-slate-100"
                  } bg-transparent text-gray-600`}
                  onClick={toggleGroupSegment}
                >
                  <FaLayerGroup className="text-md" />
                  <span className="text-md">
                    {viewMode === "groups" ? "Ads" : "Groups"}
                  </span>
                </button>
                {isGroupDropdownVisible && (
                  <div className="absolute right-0 bg-white shadow-md rounded p-4 mt-2 z-40 border w-64 space-y-2">
                    {savedGroups && savedGroups.length > 0 ? (
                      <>
                        <FormControl fullWidth>
                          <InputLabel>{currentGroupType} Group</InputLabel>
                          <Select
                            multiple
                            value={selectedGroupIds}
                            onChange={handleGroupChange}
                            input={
                              <OutlinedInput
                                label={currentGroupType + " Group"}
                              />
                            }
                            renderValue={getSelectedGroupNames}
                            MenuProps={{ className: "groupwise-select-menu" }}
                          >
                            {savedGroups.map((data) => (
                              <MenuItem
                                key={data.group_id}
                                value={data.group_id}
                              >
                                <Checkbox
                                  checked={selectedGroupIds.includes(
                                    data.group_id,
                                  )}
                                />
                                <ListItemText primary={data.group_name} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <Button
                          variant="contained"
                          color="primary"
                          disabled={!selectedGroupIds?.length}
                          onClick={() => {
                            setViewMode("groups");
                            setSegmentType("default");
                            toggleDropDownTabs("Hide all");
                          }}
                          className="mt-2"
                        >
                          Apply
                        </Button>
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center p-6 text-center">
                        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                          <FaLayerGroup className="text-gray-400 text-lg" />
                        </div>

                        <p className="text-sm font-medium text-gray-700">
                          No groups available
                        </p>

                        <p className="mt-1 text-xs text-gray-500 leading-relaxed">
                          You don’t have any groups created yet. Please create a
                          group to manage and apply group-based ads.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div
                className={`relative transition-opacity duration-300 ${isLoading ? "pointer-events-none opacity-50" : ""}`}
                ref={downloadRef}
              >
                <button
                  disabled={isLoading || isDownloading}
                  className={`px-4 py-2 min-w-[80px] rounded flex flex-col justify-center items-center gap-1 transition-all ${
                    isLoading || isDownloading
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-slate-100"
                  } ${showDownloadMenu ? "bg-blue-100 text-blue-600" : "bg-transparent text-gray-600"}`}
                  onClick={() => toggleDropDownTabs("Download")}
                >
                  {isDownloading ? (
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    <MdOutlineFileDownload className="text-md" />
                  )}
                  <span className="text-md">
                    {isDownloading ? "Downloading" : "Download"}
                  </span>
                </button>
                {showDownloadMenu && (
                  <div className="absolute right-0 z-40 w-36 bg-white shadow-lg rounded-lg mt-2 p-2 border">
                    <div
                      className="p-2 hover:bg-gray-100 cursor-pointer rounded flex items-center justify-between text-gray-700"
                      onClick={() => handleDownload("pdf")}
                    >
                      .PDF
                    </div>
                    <div
                      className="p-2 hover:bg-gray-100 cursor-pointer rounded flex items-center justify-between text-gray-700"
                      onClick={() => handleDownload("csv")}
                    >
                      .CSV
                    </div>
                  </div>
                )}
              </div>

              <button
                disabled={isLoading}
                className={`px-4 py-2 min-w-[80px] rounded flex flex-col justify-center items-center gap-1 transition-all ${
                  isFullScreen
                    ? "bg-blue-100 text-blue-600"
                    : "bg-transparent text-gray-600"
                } ${isLoading ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-100"}`}
                onClick={toggleFullScreen}
              >
                {isFullScreen ? (
                  <FaCompress className="text-md" />
                ) : (
                  <FaExpand className="text-md" />
                )}
                <span className="text-md">
                  {isFullScreen ? "Collapse" : "Expand"}
                </span>
              </button>
              <div className="relative" ref={ColumnRef}>
                <button
                  disabled={isLoading}
                  className={`px-4 py-2 min-w-[80px] rounded flex flex-col justify-center items-center gap-1 transition-all ${
                    isLoading
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-slate-100"
                  } bg-transparent text-gray-600`}
                  onClick={() => toggleDropDownTabs("Columns")}
                >
                  <FaColumns className="text-md" />
                  <span className="text-md">Columns</span>
                </button>
                {showColumnsMenu && (
                  <ModifyColumns
                    columns={columns}
                    setColumns={setColumns}
                    setTableVisible={setTableVisible}
                    setTotalShow={setTotalShow}
                    setTotal={setTotal}
                    setShowColumnsMenu={setShowColumnsMenu}
                  />
                )}
              </div>
            </div>
          </div>

          {tableVisible && (
            <div>
              {filterArray.length > 0 && (
                <div className="w-full flex flex-row space-x-4 items-center p-2 relative">
                  <div className="flex flex-row w-11/12 overflow-auto scroll-container">
                    {filterArray.map((item) => (
                      <div
                        key={item.id}
                        className="flex flex-row items-center justify-between p-2 border border-black rounded-lg min-w-fit mx-1"
                      >
                        <h2 className="mr-2">
                          {item.title} {item.sign}{" "}
                          {Array.isArray(item.value)
                            ? item.value.join(", ")
                            : item.value}
                        </h2>
                        <button onClick={() => removeFilterFromArray(item.id)}>
                          <IoMdClose />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="clearfilter flex item-center justify-center absolute right-5 text-2xl hover:bg-red-500 hover:text-white">
                    <button onClick={() => setFilterArray([])}>
                      <IoMdClose className="ml-5" />
                    </button>
                  </div>
                </div>
              )}
              {error ? (
                <div className=" text-center">No data found</div>
              ) : isLoading ? (
                <div className="mt-4">
                  <TableSkeleton />
                </div>
              ) : (
                <div className="overflow-auto w-full max-h-[calc(100vh-250px)] scrollbar-thin scrollbar-track-gray-200 scrollbar-thumb-gray-400">
                  {showInitialData &&
                    (data.length === 0 ? (
                      <div className="flex justify-center items-center h-40 mt-8">
                        <span className="text-gray-500">No data available</span>
                      </div>
                    ) : (
                      <RenderTable
                        columns={columns}
                        data={filteredData}
                        variant="performance"
                        enableExpandCollapse={state.length > 1}
                        currentPage={currentPage}
                        rowsPerPage={rowsPerPage}
                      />
                    ))}
                </div>
              )}
              <div className="flex justify-end items-center mt-4 font-serif p-3 rounded-lg">
                <div className="flex items-center space-x-2 mr-4">
                  <label
                    className={`text-gray-700 ${isLoading ? "opacity-50" : ""}`}
                  >
                    Rows per page:
                  </label>
                  <select
                    disabled={isLoading}
                    value={rowsPerPage}
                    onChange={handleRowsPerPageChange}
                    className={`border border-gray-300 rounded px-2 py-1 ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="15">15</option>
                  </select>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    disabled={currentPage === 1 || isLoading}
                    onClick={() => handlePageChange(currentPage - 1)}
                    className={`px-1 py-1 text-gray-700 rounded transition-all ${
                      currentPage === 1 || isLoading
                        ? "cursor-not-allowed opacity-50"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    <FaChevronLeft />
                  </button>
                  <span className={isLoading ? "opacity-50" : ""}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    disabled={currentPage >= totalPages || isLoading}
                    onClick={() => handlePageChange(currentPage + 1)}
                    className={`px-1 py-1 text-gray-700 font-bold rounded transition-all ${
                      currentPage >= totalPages || isLoading
                        ? "cursor-not-allowed opacity-50"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    <FaChevronRight />
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};
export default Ads;
